import { createPopper, type Instance } from "@popperjs/core";
import { Controller } from "@hotwired/stimulus";

let counter = 0;
// One disclosure is open at a time, like the live UI's popovers.
let closeCurrent: (() => void) | null = null;

// Connects to data-controller="tooltip". A click trigger is a disclosure (see TooltipComponent): aria-expanded on the
// button, the panel takes focus when it opens, and Escape or a press outside closes it with focus back on the button.
// Any other trigger is a hover tooltip that describes the button. Popper is created on first show, so a list with
// dozens of tooltips costs nothing until one is opened.
export default class extends Controller<HTMLElement> {
	static targets = ["element", "tooltip"];

	declare readonly elementTarget: HTMLElement;
	declare readonly tooltipTarget: HTMLElement;

	private trigger: string | null = null;
	private popper: Instance | null = null;
	private open = false;
	private showHandler = (event: Event) => this.showTooltip(event);
	private hideHandler = () => this.hideTooltip();
	private outsideHandler = (event: Event) => this.closeOutside(event);
	private escapeHandler = (event: KeyboardEvent) => this.closeOnEscape(event);
	private cacheHandler = () => this.hideTooltip();
	private events: string[] = [];

	connect() {
		this.tooltipTarget.id ||= `tooltip-${++counter}`;
		this.setupEvent();
		// A Turbo snapshot taken with the panel open comes back open, with no listeners behind it: close before the
		// snapshot, and start closed whatever a restored page says.
		document.addEventListener("turbo:before-cache", this.cacheHandler);
		this.hideTooltip();
	}

	disconnect() {
		for (const event of this.events) {
			this.elementTarget.removeEventListener(event, this.showHandler);
			this.elementTarget.removeEventListener(event, this.hideHandler);
		}
		this.removeDismissListeners();
		document.removeEventListener("turbo:before-cache", this.cacheHandler);
		if (closeCurrent === this.hideHandler) closeCurrent = null;
		this.popper?.destroy();
		this.popper = null;
	}

	private get disclosure() {
		return this.trigger === "click";
	}

	private setupEvent() {
		this.trigger = this.elementTarget.getAttribute("data-tooltip-trigger");
		if (this.disclosure) {
			this.elementTarget.setAttribute("aria-controls", this.tooltipTarget.id);
			this.events = ["click"];
			this.elementTarget.addEventListener("click", this.showHandler);
		} else {
			this.elementTarget.setAttribute("aria-describedby", this.tooltipTarget.id);
			this.events = ["mouseenter", "mouseleave", "focus", "blur"];
			this.elementTarget.addEventListener("mouseenter", this.showHandler);
			this.elementTarget.addEventListener("focus", this.showHandler);
			this.elementTarget.addEventListener("mouseleave", this.hideHandler);
			this.elementTarget.addEventListener("blur", this.hideHandler);
		}
	}

	private getPlacement() {
		const placement = this.tooltipTarget.getAttribute("data-tooltip-placement");
		return placement && ["auto", "top", "right", "bottom", "left"].includes(placement) ? (placement as "auto") : "auto";
	}

	private createPopper() {
		return createPopper(this.elementTarget, this.tooltipTarget, {
			placement: this.getPlacement(),
			modifiers: [{ name: "offset", options: { offset: [0, 8] } }],
		});
	}

	private async showTooltip(event: Event) {
		event.preventDefault();
		event.stopPropagation();
		if (this.open) {
			if (this.disclosure) this.hideTooltip();
			return;
		}
		if (this.disclosure) {
			closeCurrent?.();
			closeCurrent = this.hideHandler;
			document.addEventListener("click", this.outsideHandler);
			document.addEventListener("keydown", this.escapeHandler, true);
		}
		// A closed panel is display:none so it takes no layout; Popper measures it while it is shown but still invisible.
		this.tooltipTarget.classList.remove("hidden");
		this.popper ??= this.createPopper();
		await this.popper.update();
		this.tooltipTarget.classList.remove("invisible");
		this.open = true;
		if (this.disclosure) {
			this.elementTarget.setAttribute("aria-expanded", "true");
			this.tooltipTarget.focus({ preventScroll: true });
		}
	}

	private hideTooltip() {
		this.tooltipTarget.classList.add("hidden", "invisible");
		this.open = false;
		this.removeDismissListeners();
		if (this.disclosure) {
			this.elementTarget.setAttribute("aria-expanded", "false");
			if (closeCurrent === this.hideHandler) closeCurrent = null;
		}
	}

	private removeDismissListeners() {
		document.removeEventListener("click", this.outsideHandler);
		document.removeEventListener("keydown", this.escapeHandler, true);
	}

	// A press inside the panel (a link, selecting text) leaves it open.
	private closeOutside(event: Event) {
		if (!this.tooltipTarget.contains(event.target as Node)) this.hideTooltip();
	}

	// Heard in the capture phase and consumed, so the key does nothing else behind the panel.
	private closeOnEscape(event: KeyboardEvent) {
		if (event.key !== "Escape") return;
		event.preventDefault();
		event.stopPropagation();
		this.hideTooltip();
		this.elementTarget.focus();
	}
}
